import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Sparkles,
  Send,
  X,
  Loader2,
  AlertTriangle,
  User,
  Bot,
} from "lucide-react";
import { helperChat } from "@/api/endpoints/helper";

// Floating bottom-right widget. Click bubble → opens a chat panel that
// posts to /api/v1/helper/chat (which proxies to OMER) with the
// current page route + active tenant baked in for grounded answers.

interface Message {
  role: "user" | "assistant";
  text: string;
}

interface HelperWidgetProps {
  /** Optional active tenant slug, passed as context to OMER. */
  tenantSlug?: string;
}

const STORAGE_KEY = "alphaprox.helper.history";
const MAX_HISTORY = 30;

function loadHistory(): Message[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Message[];
    return Array.isArray(parsed) ? parsed.slice(-MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: Message[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-MAX_HISTORY)));
  } catch {
    // quota; drop silently
  }
}

// Public hook for other components (e.g. PageHelp) to pre-fill the
// chat box without re-implementing the open/close handshake.
let openHandler: ((prefill?: string) => void) | null = null;
export function openHelper(prefill?: string) {
  openHandler?.(prefill);
}

export default function HelperWidget({ tenantSlug }: HelperWidgetProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [history, setHistory] = useState<Message[]>(loadHistory());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    openHandler = (prefill?: string) => {
      setOpen(true);
      if (prefill) setInput((prev) => (prev ? prev : prefill));
    };
    return () => {
      openHandler = null;
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, open]);

  const chatMut = useMutation({
    mutationFn: (msg: string) =>
      helperChat({
        message: msg,
        page: location.pathname,
        tenant_slug: tenantSlug,
        conversation_id: conversationId ?? undefined,
      }),
    onSuccess: (resp, msg) => {
      const next = [
        ...history,
        { role: "user" as const, text: msg },
        { role: "assistant" as const, text: resp.answer },
      ];
      setHistory(next);
      saveHistory(next);
      if (resp.conversation_id) setConversationId(resp.conversation_id);
    },
  });

  const send = () => {
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    chatMut.mutate(msg);
  };

  const reset = () => {
    setHistory([]);
    setConversationId(null);
    saveHistory([]);
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Assistant AlphaProx"
          className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-500"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-30 flex h-[600px] w-96 max-w-[calc(100vw-3rem)] flex-col rounded-lg border border-gray-700 bg-gray-900 shadow-2xl">
          <header className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-100">
              <Sparkles className="h-4 w-4 text-blue-400" />
              Assistant AlphaProx
            </h3>
            <div className="flex items-center gap-1">
              <button
                onClick={reset}
                title="Effacer la conversation"
                className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-800 hover:text-gray-300"
              >
                clear
              </button>
              <button
                onClick={() => setOpen(false)}
                className="rounded p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </header>

          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto p-4"
          >
            {history.length === 0 && !chatMut.isPending && (
              <div className="space-y-2 text-sm text-gray-400">
                <p>
                  Bonjour ! Je peux repondre a tes questions sur AlphaProx
                  (onboarding, users, vault, IPAM, audit, etc.) avec le
                  contexte de la page courante.
                </p>
                <p className="text-xs text-gray-500">
                  Exemples : "comment je cree un user ?", "qu'est-ce que
                  reprovision ?", "ou je trouve les backups ?"
                </p>
              </div>
            )}
            {history.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === "user"
                    ? "ml-6 rounded-lg bg-blue-900/40 px-3 py-2"
                    : "mr-6 rounded-lg bg-gray-800 px-3 py-2"
                }
              >
                <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">
                  {m.role === "user" ? (
                    <User className="h-3 w-3" />
                  ) : (
                    <Bot className="h-3 w-3" />
                  )}
                  {m.role === "user" ? "Vous" : "Assistant"}
                </div>
                <div className="whitespace-pre-wrap text-sm text-gray-200">
                  {m.text}
                </div>
              </div>
            ))}
            {chatMut.isPending && (
              <div className="mr-6 flex items-center gap-2 rounded-lg bg-gray-800 px-3 py-2 text-sm text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                L'assistant reflechit...
              </div>
            )}
            {chatMut.isError && (
              <div className="mr-6 flex items-start gap-2 rounded-lg bg-red-900/40 px-3 py-2 text-xs text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>{(chatMut.error as Error).message}</span>
              </div>
            )}
          </div>

          <footer className="border-t border-gray-800 p-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Une question..."
                disabled={chatMut.isPending}
                className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!input.trim() || chatMut.isPending}
                className="flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </form>
            <div className="mt-1.5 truncate text-[10px] text-gray-600">
              Contexte : {location.pathname}
              {tenantSlug ? ` · tenant=${tenantSlug}` : ""}
            </div>
          </footer>
        </div>
      )}
    </>
  );
}
